import {
  AvatarSkeleton,
  BottomSheet,
  Illustration,
  TextSkeleton,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogActions, DialogContent, Stack } from "@mui/material";
import { DEFAULT_SUPPORT_LINKS, SupportContext } from "@src/appV2/support/constants";
import { ContactSupportButton } from "@src/appV2/support/ContactSupportButton";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { Button } from "../../components/Button";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";

interface QuizFinishedBottomSheetProps {
  modalState: UseModalState;
  isLoading: boolean;
  isError: boolean;
  userPassedQuiz: boolean;
  stillMissingRequirements: boolean;
  isBundle?: boolean;
  onClose: () => void;
  onRetakeQuiz: () => void;
  onBookShift: () => void;
}

export function QuizFinishedBottomSheet(props: QuizFinishedBottomSheetProps) {
  const {
    modalState,
    isLoading,
    isError,
    userPassedQuiz,
    stillMissingRequirements,
    isBundle = false,
    onClose,
    onRetakeQuiz,
    onBookShift,
  } = props;

  function getIllustration() {
    if (isLoading) {
      return <AvatarSkeleton size="large" />;
    }

    if (isError) {
      return <Illustration type="quiz-error" />;
    }

    if (userPassedQuiz) {
      return <Illustration type="quiz-success" />;
    }

    return <Illustration type="quiz-error" />;
  }

  function getHeadline() {
    const variant = "h2";

    if (isLoading) {
      return (
        <Stack alignItems="center">
          {Array.from({ length: 2 }).map((_, index) => (
            // oxlint-disable-next-line react/no-array-index-key -- Static skeleton elements
            <TextSkeleton key={index} variant={variant} width="50%" />
          ))}
        </Stack>
      );
    }

    let texts: string[];

    if (isError) {
      texts = ["Internal Error"];
    } else if (userPassedQuiz) {
      texts = ["You passed", "the quiz!"];
    } else {
      texts = ["You didn't pass", "the quiz!"];
    }

    return (
      <Stack>
        {texts.map((text) => (
          <Title key={text} component={variant} textAlign="center" paddingX={10}>
            {text}
          </Title>
        ))}
      </Stack>
    );
  }

  function getDescription() {
    const variant = "h6";

    if (isLoading) {
      return (
        <Stack alignItems="center">
          {Array.from({ length: 4 }).map((_, index) => (
            // oxlint-disable-next-line react/no-array-index-key -- Static skeleton elements
            <TextSkeleton key={index} variant={variant} width="80%" />
          ))}
        </Stack>
      );
    }

    let texts: string[];
    if (isError) {
      texts = [
        "There was an error, during the quiz submission. Sorry! Please try again later or contact our support.",
      ];
    } else if (userPassedQuiz) {
      texts = [
        "You're now able to book instantly at this facility, if you meet the requirements for a shift. You won't need to take the quiz again unless this facility adds more information or rules.",
      ];
    } else {
      texts = [
        "Unfortunately, you didn't answer enough questions correctly to pass the quiz and book shifts at Millennium Memory Care At Freehold.",
        "You can retake the quiz to book this shift. Once you pass the quiz you won't need to take it again unless the information changes.",
      ];
    }

    return (
      <Stack>
        {texts.map((text) => (
          <Title key={text} component={variant} textAlign="center" paddingX={10}>
            {text}
          </Title>
        ))}
      </Stack>
    );
  }

  function getButton() {
    if (isLoading) {
      return <Button fullWidth disabled />;
    }

    if (isError) {
      return (
        <ContactSupportButton
          supportContext={SupportContext.WORKPLACE_QUIZ_ERROR}
          supportLink={DEFAULT_SUPPORT_LINKS.HOW_TO_CONTACT_SUPPORT}
        >
          Contact Support
        </ContactSupportButton>
      );
    }

    if (userPassedQuiz) {
      if (stillMissingRequirements) {
        return (
          <Button fullWidth onClick={onClose}>
            Close
          </Button>
        );
      }

      return (
        <Button fullWidth onClick={onBookShift}>
          Book Shift{isBundle ? "s" : ""}
        </Button>
      );
    }

    return (
      <Button fullWidth onClick={onRetakeQuiz}>
        Retake Quiz
      </Button>
    );
  }

  return (
    <BottomSheet modalState={modalState} onClose={onClose}>
      <BottomSheetHeader onClose={onClose} />
      <DialogContent>
        <Stack spacing={5}>
          <Stack alignItems="center">{getIllustration()}</Stack>

          {getHeadline()}

          {getDescription()}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={(theme) => getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(5) })}
      >
        {getButton()}
      </DialogActions>
    </BottomSheet>
  );
}
